import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/lib/locale-init";
import { useEffect, useState, type ReactNode } from "react";
import appCss from "../styles.css?url";
import favicon from "/favicon.png?url";
import { Toaster } from "@/components/ui/sonner";
import { CarregandoNavegacao } from "@/components/CarregandoNavegacao";
import { reportClientError } from "../lib/error-reporting";
import { iniciarEscutaLogsServidor } from "@/lib/logs-servidor";
import devConfig from "@/../config/dev.config.json";
import { carregarConfiguracaoDev } from "@/lib/dev-config";
import { enviarErroCliente } from "@/lib/client-error.functions";
import {
  getSessionItem,
  getLocalItem,
  removeSessionItem,
  removeLocalItem,
} from "@/lib/storage-safe";
import { carregarIPAcesso } from "@/lib/info-device";
import { carregarIconesCache } from "@/lib/icones-cache";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-[10px]">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportClientError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-[10px]">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Milky Moo Palmares — Controle de estoque para sorveteria" },
      {
        name: "description",
        content: "Controle de insumos, receitas, vendas e alertas de estoque para sorveterias.",
      },
      { name: "author", content: "Milky Moo" },
      { property: "og:title", content: "Milky Moo Palmares" },
      { property: "og:description", content: "Controle de estoque e custos para sorveterias." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@MilkyMoo" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: favicon, type: "image/png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    void carregarIPAcesso();
    void carregarIconesCache();
  }, []);

  // Inicia a escuta do diário de bordo do servidor o mais cedo possível,
  // inclusive na tela de login, para que o buffer de inicialização chegue
  // ao console do navegador sem depender de autenticação.
  useEffect(() => {
    void carregarConfiguracaoDev()
      .finally(() => iniciarEscutaLogsServidor())
      .catch(() => {});

    // Captura erros não tratados no cliente e envia para o servidor para
    // diagnóstico (não inclui segredos).
    const onError = (ev: ErrorEvent) => {
      try {
        void enviarErroCliente({
          data: { message: ev.message, details: ev.error?.stack ?? String(ev) },
        });
      } catch {}
    };
    const onRejection = (ev: PromiseRejectionEvent) => {
      try {
        const details =
          ev.reason?.stack ??
          (typeof ev.reason === "string" ? ev.reason : JSON.stringify(ev.reason));
        void enviarErroCliente({ data: { message: "unhandledrejection", details } });
      } catch {}
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection as EventListener);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection as EventListener);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Indicador leve durante a transição entre telas */}
      <CarregandoNavegacao />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <DevDiagnostic />
      <Toaster position="bottom-right" richColors />
    </QueryClientProvider>
  );
}

function DevDiagnostic() {
  // Hooks must be called unconditionally; guard behavior inside effects.
  const [, setTick] = useState(0);
  // Só renderiza depois de montar no cliente: evita divergência de hidratação
  // (o servidor não tem session/localStorage) — erro React #418.
  const [montado, setMontado] = useState(false);

  useEffect(() => setMontado(true), []);

  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  // Patch fetch to count pending requests (dev only)
  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    try {
      const w = window as any;
      if (w.__fetchPatched) return;
      w.__pendingFetchCount = 0;
      const orig = w.fetch.bind(window);
      w.fetch = async (...args: any[]) => {
        try {
          w.__pendingFetchCount = (w.__pendingFetchCount || 0) + 1;
          const r = await orig(...args);
          return r;
        } finally {
          w.__pendingFetchCount = Math.max(0, (w.__pendingFetchCount || 1) - 1);
        }
      };
      w.__fetchPatched = true;
    } catch {
      // ignore
    }
  }, []);

  // Use safe accessors to avoid SSR/client mismatches when storage is absent.
  const session = (() => {
    try {
      return getSessionItem("auth-externo-sessao");
    } catch {
      return null;
    }
  })();
  const local = (() => {
    try {
      return getLocalItem("auth-externo-sessao");
    } catch {
      return null;
    }
  })();
  const pending = typeof window !== "undefined" ? ((window as any).__pendingFetchCount ?? 0) : 0;

  if (!montado || !import.meta.env.DEV || !devConfig?.janelaDevDiagNoCanto) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 12,
        bottom: 12,
        zIndex: 99999,
        background: "rgba(0,0,0,0.8)",
        color: "#fff",
        padding: "8px",
        borderRadius: 8,
        fontSize: 12,
        fontFamily: "monospace",
        maxWidth: 420,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <strong style={{ fontSize: 12 }}>DEV DIAG</strong>
        <span style={{ marginLeft: "auto", opacity: 0.8 }}>pending fetchs: {pending}</span>
      </div>
      <div style={{ maxHeight: 160, overflow: "auto", whiteSpace: "pre-wrap" }}>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600 }}>sessionStorage:</div>
          <div>{session ?? "(empty)"}</div>
        </div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontWeight: 600 }}>localStorage:</div>
          <div>{local ?? "(empty)"}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          onClick={() => {
            try {
              removeSessionItem("auth-externo-sessao");
              removeLocalItem("auth-externo-sessao");
            } catch {}
            location.reload();
          }}
          style={{ padding: "6px 8px", borderRadius: 6, border: "none", cursor: "pointer" }}
        >
          Clear session
        </button>
        <button
          onClick={() => {
            try {
              // open logs in new tab
              // eslint-disable-next-line no-restricted-syntax
              window.open("/_serverFn/logs", "_blank");
            } catch {}
          }}
          style={{ padding: "6px 8px", borderRadius: 6, border: "none", cursor: "pointer" }}
        >
          Open logs
        </button>
      </div>
    </div>
  );
}
